// Centralized PostHog tracking for the dashboard.
//
// Call useTracking() in a valid Nuxt context (setup of a <script setup>,
// or at the very SYNCHRONOUS start of an Options API handler), then use the
// returned helpers, including in async callbacks: the Nuxt instance is
// captured only once, so the functions stay callable later.
export const useTracking = () => {
  const nuxtApp = useNuxtApp();
  const ph = () => nuxtApp.$posthog?.();

  // Properties added automatically to every event (account/user context).
  const globalProperties = () => ({
    account_id: nuxtApp.$account?.id ?? null,
    subscription_plan: nuxtApp.$account?.subscription_plan ?? null,
    is_admin: !!nuxtApp.$user?.isAdmin,
    page_path: nuxtApp.$router?.currentRoute?.value?.path ?? null
  });

  const track = (event, properties = {}) => {
    ph()?.capture(event, { ...globalProperties(), ...properties });
  };

  // GTM push (container GTM-PL632P34). Feeds window.dataLayer for Google Tag
  // Manager, in addition to the PostHog capture. Idempotent if GTM is not loaded
  // yet: the dataLayer is initialized when needed.
  const pushDataLayer = (payload) => {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(payload);
  };

  // --- Orders ---
  const trackOrderValidated = ({ order_id = null, reference = null, item_count = null, amount = null, carrier_id = null } = {}) =>
    track("order_validated", { order_id, reference, item_count, amount, currency: "EUR", carrier_id });

  const trackOrderDraftSaved = ({ order_id = null, reference = null, item_count = null, amount = null } = {}) =>
    track("order_draft_saved", { order_id, reference, item_count, amount });

  const trackOrderFileUploaded = ({ file_name = null, file_size = null, side = null } = {}) =>
    track("order_file_uploaded", { file_name, file_size, side });

  const trackOrderDeleted = ({ order_id = null, order_status = null } = {}) =>
    track("order_deleted", { order_id, order_status });

  const trackOrderDuplicated = ({ order_id = null } = {}) =>
    track("order_duplicated", { order_id });

  const trackShippingGenerated = ({ order_id = null } = {}) =>
    track("shipping_generated", { order_id });

  // --- Credits & payments ---
  const trackCreditAdded = ({ amount = null, new_balance = null } = {}) =>
    track("credit_added", { amount, currency: "EUR", new_balance });

  const trackCreditQuoteRequested = ({ amount = null } = {}) =>
    track("credit_quote_requested", { amount });

  // Starts adding a payment method (Stripe Checkout redirect).
  const trackPaymentMethodAdded = (properties = {}) =>
    track("payment_method_added", { ...properties });

  // --- Subscriptions ---
  const trackSubscriptionStarted = ({ plan_id = null, plan_name = null, plan_price = null } = {}) => {
    track("subscription_started", { plan_id, plan_name, plan_price, currency: "EUR" });
    pushDataLayer({ event: "subscription_started", plan_price });
  };

  // --- Sandbox ---
  // Orders created through the API appear in no funnel (all tracking is client-side): these two
  // events at least measure what the dashboard onboarding does.
  const trackSandboxOrderCreated = ({ order_id = null } = {}) =>
    track("sandbox_order_created", { order_id });

  const trackSandboxOrderSimulated = ({ order_id = null, status = null } = {}) =>
    track("sandbox_order_simulated", { order_id, status });

  // --- Team ---
  const trackTeamMemberInvited = ({ invited_email_domain = null } = {}) =>
    track("team_member_invited", { invited_email_domain });

  // --- Auth ---
  const trackAccountRegistered = ({ company = null, country = null } = {}) => {
    track("account_registered", { company, country });
    pushDataLayer({ event: "account_registered" });
  };

  // --- Onboarding ---
  // The funnel from sign-up to plan, screen by screen: where people drop. Only codes leave, never what was typed.
  const trackEmailVerified = () => track("email_verified");
  const trackOnboardingStep = ({ step = null, value = null } = {}) => track("onboarding_step_completed", { step, value });
  const trackOnboardingAnswered = ({ role = null, team = null, goal = null, volume = null, has_website = false } = {}) =>
    track("onboarding_answered", { role, team, goal, volume, has_website });
  // A question to the team (the topic and the channel, never the message): where people get stuck.
  const trackHelpRequested = ({ topic = null, callback = false } = {}) => track("help_requested", { topic, callback });
  // The getting started left for the full dashboard, or resumed: how many leave it, and from which step.
  const trackSetupModeChanged = ({ mode = null, next_step = null } = {}) => track("setup_mode_changed", { mode, next_step });
  // --- Leaving ---
  // Each step of the cancellation path (opened, reason, answer offered or taken, confirmed, withdrawn): where people
  // leave, why, and what keeps them. Codes only, never the comment.
  const trackCancellation = ({ step = null, reason = null, answer = null } = {}) => track("plan_cancellation", { step, reason, answer });

  return {
    track,
    trackEmailVerified,
    trackOnboardingStep,
    trackOnboardingAnswered,
    trackHelpRequested,
    trackSetupModeChanged,
    trackCancellation,
    trackOrderValidated,
    trackOrderDraftSaved,
    trackOrderFileUploaded,
    trackOrderDeleted,
    trackOrderDuplicated,
    trackShippingGenerated,
    trackCreditAdded,
    trackCreditQuoteRequested,
    trackPaymentMethodAdded,
    trackSubscriptionStarted,
    trackSandboxOrderCreated,
    trackSandboxOrderSimulated,
    trackTeamMemberInvited,
    trackAccountRegistered
  };
};
